import React, { useCallback, useMemo } from "react";
import Text from 'xpi-black-ui/Base/Typography/Text';
import GenericInput from '../../Generics/GenericInput';
import NumberBadge from '../../Utils/NumberBadge';
import WrapperDialog, {
  WrapperHeader,
  WrapperDialogContent,
  WrapperDialogContentLegs,
  WrapperDialogContentInformation,
  WrapperPositionInformation,
  WrapperLegInformation,
  WrapperLegInfo
} from './styles';

export default function UnwindingDetailsDialog({ onClose, position, isOpen }) {
  const isCurrentProfitWithSellAsset = useMemo(() => position?.structure?.hasAsset, [position?.structure?.hasAsset]);

  const PositionInformation = useCallback(({ title, value, isFullWidth }) => (
    <WrapperPositionInformation isFullWidth={isFullWidth}>
      <GenericInput title={title} defaultValue={`${value}`} isReadOnly />
    </WrapperPositionInformation>
  ), []);

  const LegInformation = useCallback(({ index, info }) => (
    <WrapperLegInformation>
      <NumberBadge value={index + 1} />
      <WrapperLegInfo>
        <Text bigger>{info.option}</Text>
          {info.strike && <Text bigger>{info.strike}</Text>}
          {info.barrier && <Text bigger>{info.barrier}</Text>}
      </WrapperLegInfo>
    </WrapperLegInformation>
  ), []);

  const LegFixedInformation = useCallback(({ index, value }) => (
    <WrapperLegInformation>
      <NumberBadge value={index} />
      <WrapperLegInfo>
        <Text bigger>{value}</Text>
      </WrapperLegInfo>
    </WrapperLegInformation>
  ), []);

  const getLegInformation = useCallback(({ optionType, barrierType, currentBarrier, strike, side, quantityCurrent }) => {
    const IS_SPOT = optionType.toUpperCase() === 'SPOT';
    const STRIKE_TITLE = IS_SPOT ? '' : `Strike ${strike.percentage} / ${strike.value}`;
    const BARRIER_TITLE = barrierType ? `Barreira ${barrierType} ${currentBarrier.percentage} / ${currentBarrier.value}` : '';

    return {
      option: `${side} ${optionType} - Qtde Atual: ${quantityCurrent}`,
      strike: STRIKE_TITLE,
      barrier: BARRIER_TITLE
    }
  }, []);

  return (
    <WrapperDialog overlayClick={onClose} open={isOpen}>
      <WrapperHeader onClose={onClose} title={`Detalhes da Antecipação (#${position?.operationId})`} />

      <WrapperDialogContent>
        <WrapperDialogContentLegs>
          {position?.legs?.map(({
            barrierType,
            currentBarrier,
            optionType,
            side,
            strike,
            quantityCurrent,
          }, index) => (
            <LegInformation
              key={`${optionType}-${side}`}
              index={index}
              info={getLegInformation({
                side,
                optionType,
                barrierType,
                currentBarrier,
                strike,
                quantityCurrent
              })}
            />
          ))}
          <LegFixedInformation key='fixing' index={position?.legs?.length + 1} value={`Data de Encerramento - ${position?.maturityDate}`} />
        </WrapperDialogContentLegs>

        <WrapperDialogContentInformation>
          <PositionInformation title="Cliente" value={position?.clientId} />
          <PositionInformation title="Ativo" value={position?.asset?.ticker} />

          <PositionInformation title="Estrutura" value={position?.structure?.name} />
          <PositionInformation title="Quantidade Contratada" value={position?.quantityContracted} />

          <PositionInformation title="Data de Entrada" value={position?.acquisitionDate} />
          <PositionInformation title="Tempo na Operação" value={position?.elapsedOperationTime?.inText} />

          <PositionInformation title="Preço de Entrada" value={position?.asset?.acquisitionSpotValue} />
          <PositionInformation title="Ganho Máximo Contratado (%)" value={position?.acquisitionInfo?.maxProfitPercentage} />

          <PositionInformation
            title={`Valor Desembolsado na Entrada (${isCurrentProfitWithSellAsset ? 'Ações + Opções' : 'Opções' })`}
            value={position?.acquisitionInfo[isCurrentProfitWithSellAsset ? 'costWithAsset' : 'costWithoutAsset']}
            isFullWidth
           />

          <PositionInformation title="Financeiro Atual (Ação)" value={position?.currentPosition?.onlyWithAssetValue} />
          <PositionInformation title="Financeiro Atual (Opções)" value={position?.currentPosition?.onlyWithOptionValue} />

          <PositionInformation title="Financeiro Atual de Saída (Ações + Opções)" value={position?.currentPosition?.totalValue} isFullWidth />

          <PositionInformation title="Dividendos por Ativo" value={position?.asset?.dividendsPayed} />
          <PositionInformation title="Total de Proventos" value={position?.asset?.totalDividends} />

          <PositionInformation title="Resultado Bruto Atual (R$)" value={position?.currentPosition?.profit[position?.structure?.hasAsset ? 'withAsset' : 'withoutAsset']} />
          <PositionInformation title="Resultado Bruto Atual (%)" value={position?.currentPosition?.profit?.percentage} />
        </WrapperDialogContentInformation>
      </WrapperDialogContent>
    </WrapperDialog>
  );
}
